import { SubscriptionItemOrigin, type Country } from './enums';
import {
  BillingCycle,
  CommercialMode,
  Currency,
  LifetimeFreeCategory,
  PaymentProvider,
  Sku,
  SubscriptionStatus,
} from './enums';

/**
 * Predicados e aritmética PUROS de cobrança — a fonte única das regras que as
 * três plataformas e as Functions aplicam sobre `billing/subscription`,
 * `invoices` e `payments`.
 *
 * Origem: sem issue — commit 2ae71001f, 2026-04-30 17:38 +0200 (plano único,
 * `STANDARD_PLAN_PRICING`). Reescrito na Sprint 16 (catálogo no Firestore) e
 * expandido pela #130 (predicados de gateway), #404/#405 (ciclo de vida do
 * item), #679 (venda assistida, tipo comercial, capabilities) e #748/#755
 * (Asaas).
 *
 * Por quê: cada regra daqui já viveu copiada em 4–7 lugares com grafias
 * divergentes (`!= null` vs `!== ''`; `removed_at !== null` que pulava campo
 * ausente), e cada divergência virou bug de cobrança (#405 cobrou dobrado,
 * #463 fez o total e o drilldown discordarem). Mora em `@etiquetachef/types`,
 * e não em `apps/functions`, porque web, admin, CRM e scripts precisam da MESMA
 * resposta — e Android/iOS a espelham à mão (ver cada predicado).
 *
 * Contrato: tudo aqui é puro — sem Firestore, sem SDK de gateway, sem relógio
 * implícito (quem precisa de "agora" recebe `atMillis`/`now`). Os tipos
 * `*Fields` são ESTRUTURAIS de propósito: aceitam o objeto parseado pelo Zod E
 * o read cru do Firestore (`snap.get(...)`), onde campo novo pode não existir.
 *
 * Usado por: Functions (`billing/**`, `aggregations/**`, `admin/**`,
 * `lib/billing/**`), CRM Functions (`salesScope`), web `apps/app` (paywall,
 * `AccountBillingPage`, `SubscriptionItemsCard`), `apps/admin` (BillingCard,
 * dashboard-finance), `apps/crm` (SubscriptionTab, SaleSubscriptionPage),
 * `packages/ui-shared` (MoneyDisplay/PlanCard), `scripts/` (auditorias e
 * e2e). Espelhos à mão: Android `AppViewModel.kt`/`Subscription.kt`, iOS
 * `SessionGates.swift` — este arquivo NÃO entra no contrato gerado
 * (`npm run contract`), que cobre só enums/schemas/tabelas; mudar um predicado
 * aqui exige mudar os espelhos e os testes nas três linguagens.
 *
 * Arquitetura: unidade monetária canônica em CENTS (menor unidade da moeda)
 * em tudo que é persistido — `Subscription.amount`, `Invoice.total_amount`,
 * `Payment.amount`; UNIDADES decimais só no catálogo (`PlanPricing.amount`) e
 * na borda de UI (`priceCentsToUnits`, `formatCurrency`). Pricing congelado:
 * nada aqui lê `/config/planPrices` — o preço vem do documento da assinatura.
 * O catálogo canônico é `/config/planPrices/items/{sku}_{currency}`
 * (`apps/functions/src/lib/billing/planPricing.ts`); a grafia antiga
 * `{country}_{cycle}` deste cabeçalho deixou de existir na Sprint 35.
 *
 * Dados: os schemas Zod de `PlanPrice`, `Subscription`, `SubscriptionItem`,
 * `Invoice`, `Payment`, `PaymentMethod` e `BillingSettings` ficam em
 * `schemas.ts`, que importa ESTE módulo — por isso nada aqui importa
 * `schemas.ts` (ciclo), e `extractGrantedItems` recebe o parser do chamador.
 *
 * Falhas conhecidas:
 *  - 2026-05-08 (CR-01..CR-04): convenção mista cents/unidades — refund 100×
 *    maior que o pago. Hoje `priceUnitsToCents` é o único ponto de conversão.
 *  - 2026-09-09 (#405): `pending_removal_at` tratado como "não tem" recriava o
 *    item e cobrava dobrado — ver `isBillableItem`.
 *  - 2026-09-12 (#463): filtro de test mode no total e não no drilldown — ver
 *    `isTestModeRecord`.
 *
 * Dicas: os ciclos são QUATRO (`monthsInCycle`), não dois — trimestral e
 * semestral só existem negociados (#700). Testes em `src/*.test.ts` (vitest do
 * pacote, #267 — nasceu citando estes helpers como os sem cobertura).
 *
 * Issues: #130, #404, #405, #463, #679, #699, #700, #737, #738, #748, #750,
 * #755, #795.
 */

/**
 * Combinação preço + moeda + ciclo — a forma de um preço de LISTA do catálogo
 * `/config/planPrices/items/{sku}_{currency}` (Sprint 35), já resolvido para um
 * ciclo. Esta docstring citava `/config/planPrices/{country}_{cycle}`, grafia
 * que deixou de existir na Sprint 35 (corrigido em 2026-10-10, #798).
 *
 * **`amount` é número decimal em UNIDADES da moeda** (ex.: `19.97`,
 * `100.00`), nunca centavos. Esta é a representação amigável para
 * UI/catálogo. Quando esse valor é gravado em
 * `Subscription.amount` / `Invoice.total_amount` / `Payment.amount`
 * (que são em **CENTS** — convenção Stripe canônica do projeto),
 * converter via `priceUnitsToCents` no caller.
 *
 * Bug-hunt 2026-05-08 (CR-01..CR-04): antes a convenção era mista —
 * webhooks Stripe gravavam cents enquanto dailyBillingTick gravava
 * unidades. Refunds via `Math.round(amount*100)` aplicavam *100 em
 * cima de cents, gerando refund 100x maior do que o pago.
 */
export type PlanPricing = {
  currency: Currency;
  amount: number;
  billing_cycle: BillingCycle;
};

/**
 * Converte unidades decimais (ex.: 19.97) em cents/centesimi (1997).
 * Ponto único de conversão entre catálogo (unidades) e persistência
 * (cents). `Math.round` evita drift de float (0.1 + 0.2 ≠ 0.3 em IEEE
 * 754).
 *
 * Origem: sem issue — commit 7f2466726, 2026-05-08 12:48 +0200 (CR-01..CR-04).
 *
 * Usado por: `schemas.ts` (transforms de `PlanPriceSchema`); as Functions
 * convertem via o schema, não chamando isto direto.
 */
export function priceUnitsToCents(amount: number): number {
  return Math.round(amount * 100);
}

/**
 * Inverso para display. Use só na borda UI.
 *
 * Origem: sem issue — commit 7f2466726, 2026-05-08 12:48 +0200 (CR-01..CR-04).
 *
 * Usado por: `ui-shared` (`MoneyDisplay`, `PlanCard`), admin (`MoneyDisplay`,
 * `RequestRefundDialog`), `schemas.ts`.
 */
export function priceCentsToUnits(cents: number): number {
  return cents / 100;
}

/**
 * Forma mínima do desconto de uma assinatura necessária para calcular o
 * valor cobrado. Subconjunto estrutural de `Subscription.discount`
 * (schemas.ts), em snake_case — qualquer objeto com esses campos serve.
 */
export type DiscountForPricing = {
  /** 0–100, ou null. */
  percent_off: number | null;
  /** Abatimento fixo em cents, ou null. */
  amount_off_cents: number | null;
  /** Fim da validade do desconto (epoch ms), ou null (sem fim). */
  end_at: number | null;
  /** Isenção total (cupom mestre LIFETIME_FREE / 100% off forever). */
  is_lifetime_free: boolean;
  /**
   * #130 (7b) — duração do cupom (espelha Stripe `duration`). Opcionais: quando
   * `end_at` já está preenchido (a Stripe normalmente resolve o fim), ele vence
   * e estes são ignorados. Só entram quando `end_at === null`:
   * - `once`: vale só para a cobrança do período de `started_at`; renovações
   *   futuras a preço cheio.
   * - `repeating`: vale por `duration_in_months` a partir de `started_at`.
   * - `forever`/ausente: sem limite de tempo (comportamento legado).
   */
  duration?: 'once' | 'repeating' | 'forever' | null | undefined;
  duration_in_months?: number | null | undefined;
  started_at?: number | null | undefined;
};

/** Soma `months` meses a um epoch ms (month-accurate via Date UTC). */
function addMonthsMs(ms: number, months: number): number {
  const d = new Date(ms);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.getTime();
}

/**
 * Fim EFETIVO do desconto (epoch ms) ou `null` (sem fim). `end_at` explícito
 * vence; senão deriva de `duration`/`duration_in_months`/`started_at`.
 *
 * Origem: #130 (7b) — 2026-07-12 13:07 +0200.
 *
 * Usado por: só `discountedAmountCents` (exportado, mas sem consumidor externo
 * em 2026-10-04).
 */
export function effectiveDiscountEnd(discount: DiscountForPricing): number | null {
  if (discount.end_at !== null) return discount.end_at;
  const duration = discount.duration ?? null;
  const startedAt = discount.started_at ?? null;
  if (duration === null || duration === 'forever' || startedAt === null) return null;
  if (duration === 'once') return startedAt;
  // Só resta `repeating`: vale por `duration_in_months` a partir de started_at.
  if (discount.duration_in_months != null) {
    return addMonthsMs(startedAt, discount.duration_in_months);
  }
  return null;
}

/**
 * Valor cobrado (cents) após aplicar o desconto vigente em `atMillis`.
 *
 * **Fonte única** da regra de desconto: usada no card de assinatura do admin
 * (exibir o valor efetivo da parcela) e nos recebíveis do backend
 * (`receivablesAnalysis.applyDiscount`, que delega aqui — sem duplicar a
 * lógica). Regras:
 * - `is_lifetime_free` → 0 (cortesia vitalícia).
 * - desconto expirado (`end_at` < `atMillis`) → preço cheio.
 * - `percent_off` aplica primeiro, `amount_off_cents` abate em seguida.
 * - nunca abaixo de 0.
 *
 * Origem: sem issue — commit 5ad65970b, 2026-06-28 19:59 +0200 ("Valor da
 * parcela" do admin refletindo o desconto).
 *
 * Contrato: `amountCents` e retorno em CENTS; `atMillis` é a data da cobrança
 * avaliada (passar `Date.now()` numa projeção futura ignora um cupom `once`).
 *
 * Usado por: Functions `billing/receivablesAnalysis.ts` (`applyDiscount`) e
 * `billing/dailyBillingTick.ts`; admin `features/billing/BillingCard.tsx`.
 *
 * Histórico:
 *  - 2026-07-12 13:07 +0200 — (#130 7b) `duration` once/repeating via
 *    `effectiveDiscountEnd`.
 */
export function discountedAmountCents(
  amountCents: number,
  discount: DiscountForPricing | null,
  atMillis: number,
): number {
  if (discount === null) return amountCents;
  if (discount.is_lifetime_free) return 0;
  // Desconto expirado para esta data → preço cheio. O fim efetivo considera
  // `duration` (once/repeating) quando `end_at` não está preenchido (#130 7b).
  const end = effectiveDiscountEnd(discount);
  if (end !== null && atMillis > end) return amountCents;
  let v = amountCents;
  if (discount.percent_off !== null) {
    v = Math.round(v * (1 - discount.percent_off / 100));
  }
  if (discount.amount_off_cents !== null) {
    v = Math.max(0, v - discount.amount_off_cents);
  }
  return Math.max(0, v);
}

/**
 * Forma mínima do desconto para o predicado de isenção vitalícia —
 * subconjunto estrutural de `Subscription.discount` (schemas.ts). Aceita
 * tanto o objeto parseado quanto um read cru do Firestore
 * (`snap.get('discount')`), `null` (sem desconto) e `undefined` (campo
 * ausente).
 */
export type DiscountLifetimeFields = { is_lifetime_free?: boolean | undefined } | null | undefined;

/**
 * **Fonte única** do predicado "cortesia vitalícia?" — verdadeiro só quando
 * `discount.is_lifetime_free === true`. Antes vivia inline em ~7 pontos
 * (webhooks, tick, callables admin, recebíveis) com a mesma comparação
 * copiada; um tenant isento jamais deve ser promovido/suspenso por fatura
 * local nem contar como cobrável no MRR/forecast.
 *
 * Origem: #130 — 2026-07-12 12:15 +0200.
 *
 * Usado por: Functions `webhooks/handleCustomerDiscountCreated.ts`,
 * `receivablesAnalysis.ts`, `computeSubscriptionReceivables.ts`,
 * `admin/normalizeUnpaidSubscriptions.ts`; web `apps/app`
 * `features/billing/AccountBillingPage.tsx`.
 *
 * Dicas: para RELATAR o tipo comercial use `effectiveCommercialMode` — ele lê
 * este cupom só enquanto o `commercial_mode` não está gravado (#738).
 */
export function isLifetimeFree(discount: DiscountLifetimeFields): boolean {
  return discount?.is_lifetime_free === true;
}

/**
 * Forma mínima de um `SubscriptionItem` para os predicados de ciclo de vida —
 * subconjunto estrutural de `SubscriptionItemSchema` (schemas.ts). Aceita read
 * cru do Firestore, onde `gateway_removed_at` pode simplesmente não existir
 * (campo novo, #404).
 */
export type SubscriptionItemLifecycleFields = {
  removed_at?: number | null | undefined;
  pending_removal_at?: number | null | undefined;
  gateway_removed_at?: number | null | undefined;
};

/**
 * **Fonte única** do predicado "este item ainda vale?" — conta para cobrança
 * (`total_amount_cents`), para capability e para "o tenant já tem este SKU".
 *
 * `pending_removal_at` NÃO derruba: remoção agendada significa "contratado,
 * pago até o fim do ciclo, sai depois". Tratar isso como "não tem" foi a #405
 * — o mesmo SKU voltava a ser ofertado e um segundo item era criado, cobrando
 * dobrado. O predicado de adicionar e o de remover não são complementares;
 * para remover existe `isRemovableItem`.
 *
 * Origem: #404/#405 — 2026-09-09 14:24 +0200.
 *
 * Usado por: Functions — `webhooks/handleSubscriptionUpdated.ts`,
 * `webhooks/handleAddonProrationPaid.ts`, `webhooks/asaas/asaasHandlers.ts`,
 * `billing/pendingRemoval.ts`, todo `billing/postSale/**`,
 * `billing/asaas/installmentRenewal.ts`/`cardChange.ts`; `packages/types`
 * `postSale.ts`; web `apps/app` `SubscriptionItemsCard.tsx`;
 * `scripts/audit-duplicate-addon-items.ts`.
 *
 * Dados: `tenants/{tenantId}/billing/subscription.items[]`
 * (`SubscriptionItemSchema`, `schemas.ts`). `removed_at` é epoch ms ou `null`;
 * em documento anterior à #404 pode simplesmente não existir — e ausente conta
 * como "vale", que é a correção silenciosa que `computeCapabilities` registra.
 *
 * Falhas conhecidas:
 *  - 2026-09-09 (#405): ver acima — cobrança dobrada por tratar remoção
 *    agendada como item ausente.
 */
export function isBillableItem(item: SubscriptionItemLifecycleFields): boolean {
  const removedAt = item.removed_at;
  return removedAt === undefined || removedAt === null;
}

/**
 * **Fonte única** do predicado "este item pode receber um pedido de remoção?" —
 * exige os dois campos nulos: item já removido não se remove de novo, e item
 * com remoção agendada já está a caminho da porta.
 *
 * Origem: #404/#405 — 2026-09-09 14:24 +0200.
 *
 * Usado por: nenhum consumidor encontrado em 2026-10-04 (candidato a remoção —
 * ver #796). `postSale/itemRemoval.ts` faz a mesma checagem inline sobre
 * `pending_removal_at`.
 */
export function isRemovableItem(item: SubscriptionItemLifecycleFields): boolean {
  const pending = item.pending_removal_at;
  return isBillableItem(item) && (pending === undefined || pending === null);
}

/**
 * "Dá para desfazer a remoção agendada sem tocar no gateway?" — só enquanto o
 * passe 5 do `dailyBillingTick` ainda não removeu o item na Stripe (#404).
 * Depois disso, desfazer exigiria recriar o item, e o callable recusa com
 * `addon_removal_in_progress` (janela de ≤72 h por ciclo).
 *
 * Origem: #404 — 2026-09-09 14:24 +0200.
 *
 * Usado por: Functions `webhooks/handleSubscriptionUpdated.ts`,
 * `postSale/itemRemoval.ts`, `postSale/addSaleItem.ts`.
 */
export function isRemovalUndoable(item: SubscriptionItemLifecycleFields): boolean {
  const pending = item.pending_removal_at;
  const gatewayRemoved = item.gateway_removed_at;
  return (
    isBillableItem(item) &&
    pending !== undefined &&
    pending !== null &&
    (gatewayRemoved === undefined || gatewayRemoved === null)
  );
}

/**
 * Forma mínima de um item para as operações que falam com o gateway.
 */
export type SubscriptionItemGatewayFields = {
  external_item_id?: string | null | undefined;
  external_price_id?: string | null | undefined;
  origin?: SubscriptionItemOrigin | undefined;
};

/**
 * **Fonte única** do predicado "este item existe no gateway?" — e o type guard
 * que prova ao compilador que `external_item_id`/`external_price_id` são
 * strings dali em diante.
 *
 * Existe porque a #679 tornou os dois ids nulos (item `GRANT`, D22), e todo
 * caminho que deleta item na Stripe, pede proração ou reajusta preço de
 * catálogo passou a precisar dizer, explicitamente, o que faz com um item que
 * não está lá. O compilador cobrou cinco desses caminhos no mesmo dia — e essa
 * cobrança é o ponto: um `!` em cada um teria compilado e falhado em produção,
 * com a Stripe recusando um id nulo dentro de um `try/catch` genérico.
 *
 * Lê os ids, não `origin`: campo novo é ausente em todo documento já gravado, e
 * a pergunta verdadeira é sobre os ids, que existem desde sempre.
 *
 * Origem: #699 (D22 da #679) — 2026-09-22 16:26 +0200.
 *
 * Usado por: Functions — `webhooks/handleSubscriptionUpdated.ts`,
 * `billing/pendingRemoval.ts`, `billing/postSale/**`, `billing/asaas/saleItems.ts`,
 * `admin/reconcileSubscriptionFromGateway.ts`,
 * `lib/billing/commercialModeTransition.ts`; `packages/types` `schemas.ts`,
 * `postSale.ts`, `tenantProvisioning.ts`.
 *
 * Dados: `items[].external_item_id` (`si_…` Stripe / id Asaas) e
 * `external_price_id` (`price_…`); ambos `null` em item `GRANT` e `PARTNER`.
 */
export function isGatewayItem<T extends SubscriptionItemGatewayFields>(
  item: T,
): item is T & { external_item_id: string; external_price_id: string } {
  return (
    typeof item.external_item_id === 'string' &&
    item.external_item_id.length > 0 &&
    typeof item.external_price_id === 'string' &&
    item.external_price_id.length > 0
  );
}

/**
 * **Fonte única** da identidade de um item dentro de `items[]`.
 *
 * Era `external_item_id` e pronto — a #409 provou que por SKU não servia (dois
 * itens do mesmo SKU coexistem, e o `Map` guardava só o último). Com o item
 * concedido (#679 D22) esse id passou a poder ser `null`, e dois grants
 * colidiriam na mesma chave pelo motivo oposto: `null` é um valor só.
 *
 * A chave vale para o `Map` do diff **e** para a `key` do React — os dois
 * fazem a mesma pergunta, e tê-los divergindo seria a #409 outra vez, num
 * lugar onde o sintoma é uma linha que não atualiza em vez de um item que
 * some. Um GRANT é único por SKU dentro da assinatura: não há caminho que
 * conceda o mesmo módulo duas vezes, e `grantModuleToTenants` é idempotente
 * justamente por SKU.
 *
 * Origem: #699 — 2026-09-22 16:26 +0200.
 *
 * Usado por: Functions `webhooks/handleSubscriptionUpdated.ts` (`Map` do diff);
 * web `apps/app` `SubscriptionItemsCard.tsx` (`key` do React).
 *
 * Histórico:
 *  - 2026-09-25 17:30 +0200 — (#743) chave `partner:<sku>` para item de parceiro.
 */
export function itemIdentityKey(item: { sku: string } & SubscriptionItemGatewayFields): string {
  const externalId = item.external_item_id;
  if (typeof externalId === 'string' && externalId.length > 0) return externalId;
  // #743: item de parceiro também não tem id de gateway, e é único por SKU
  // na assinatura (o pós-venda do parceiro recusa o mesmo SKU duas vezes).
  if (item.origin === SubscriptionItemOrigin.PARTNER) return `partner:${item.sku}`;
  return `grant:${item.sku}`;
}

/**
 * Os itens CONCEDIDOS de um documento cru, para sobreviverem a um reset.
 *
 * Três caminhos reconstroem `items[]` a partir do que o gateway lista e
 * descartam o array local de propósito — o re-checkout e o enriquecimento do
 * primeiro pagamento (`handleCheckoutCompleted`) e o reinit do admin
 * (`reconcileSubscriptionFromGateway`). O reset é sancionado para o que a
 * Stripe conhece; o módulo concedido (#679 D22) **nunca esteve lá**, e passá-lo
 * adiante é o que impede esses três de apagarem, em silêncio, uma capability
 * que o cliente usa.
 *
 * Parse item a item, e não `SubscriptionSchema.safeParse(doc)`: um único campo
 * divergente no envelope faria o array inteiro virar `[]` e levar o concedido
 * junto — o modo de falha que esta fase existe para evitar. Item ilegível não é
 * propagado (propagar lixo é pior), mas volta em `unparseable` para quem chama
 * registrar, em vez de sumir.
 *
 * O schema é passado pelo chamador para este módulo não importar `schemas.ts`,
 * que já importa este (a mesma razão de `SkuSchema` ser declarado cedo lá).
 *
 * Origem: #699 — 2026-09-22 18:05 +0200 ("o quarto mecanismo").
 *
 * Contrato: `rawItems` é `snap.get('items')` cru (qualquer coisa que não seja
 * array devolve vazio); `granted` são os itens parseados SEM id de gateway
 * (`!isGatewayItem`), o que inclui `PARTNER` além de `GRANT`.
 *
 * Usado por: Functions `webhooks/handleCheckoutCompleted.ts` e
 * `admin/reconcileSubscriptionFromGateway.ts` — os caminhos que reconstroem
 * `items[]` a partir do gateway.
 */
export function extractGrantedItems<T extends SubscriptionItemGatewayFields>(
  rawItems: unknown,
  parseItem: (raw: unknown) => { success: true; data: T } | { success: false },
): { granted: T[]; unparseable: number } {
  if (!Array.isArray(rawItems)) return { granted: [], unparseable: 0 };
  const granted: T[] = [];
  let unparseable = 0;
  for (const raw of rawItems) {
    const parsed = parseItem(raw);
    if (!parsed.success) {
      unparseable += 1;
      continue;
    }
    if (!isGatewayItem(parsed.data)) granted.push(parsed.data);
  }
  return { granted, unparseable };
}

/**
 * Forma mínima de um item para derivar capability — o que `isBillableItem`
 * pede, mais o SKU.
 */
export type SubscriptionItemCapabilityFields = SubscriptionItemLifecycleFields & {
  sku: string;
  pending_proration?: unknown;
};

/**
 * "A proporção deste item ainda não foi paga?" (#737, 9.24 da #679). O item
 * já está na assinatura e conta para a fatura (`isBillableItem`), mas o
 * módulo só é liberado quando a proporção avulsa é paga.
 *
 * Origem: #737 — 2026-09-25 08:48 +0200.
 *
 * Usado por: só `computeCapabilities` (sem consumidor externo em 2026-10-04;
 * o `SubscriptionItemsCard.tsx` do app testa `pending_proration` inline).
 */
export function isAwaitingProration(item: { pending_proration?: unknown }): boolean {
  return item.pending_proration !== undefined && item.pending_proration !== null;
}

/**
 * SKUs aposentados: existem no enum (documentos antigos seguem válidos), mas nunca mais são concedidos,
 * vendidos nem despausados.
 *
 * Origem: #1043 — 2026-10-27 12:43 +0100 (owner: o `MOD_SCALE_AI` aposenta para sempre o `MOD_SCALE`,
 * "desativado sempre para todos").
 *
 * Por quê: pausar o add-on no catálogo (`disabled_at`) só barra venda nova, e qualquer super-admin pode
 * despausar — e `readIapBundleSkus` concede TODO add-on não pausado aos assinantes da loja e aos tenants
 * demo, então despausar religaria o módulo sozinho em centenas de tenants. A lista fecha isso no código.
 *
 * Contrato: lista fechada de strings; `isRetiredSku` é o predicado único. Não mexe em `computeCapabilities`:
 * quem já tem o item continua com ele até a remoção pelo pós-venda (sem reembolso, regra #755), e nenhum
 * cliente consulta a capability aposentada.
 *
 * Usado por: Functions — `iap/selfServiceProvisioning.ts` (`readIapBundleSkus`: loja, demo e
 * reconciliadores), `admin/createAddon.ts`, `admin/enableAddon.ts`, `admin/grantModuleToTenants.ts`.
 *
 * Issues: #1043 (aposentadoria), #341 (balança Bluetooth), #1014 (Balança Inteligente).
 */
export const RETIRED_SKUS: readonly string[] = [Sku.MOD_SCALE];

/** O SKU está aposentado (`RETIRED_SKUS`)? Usado pelas travas de concessão das Functions (#1043). */
export function isRetiredSku(sku: string): boolean {
  return RETIRED_SKUS.includes(sku);
}

/**
 * **Fonte única** da regra "que capability este SKU concede": `BASE` vira
 * `BASE_ACTIVE`, e todo o resto entra **cru**.
 *
 * O SKU não passa por lookup no enum `Capability` de propósito: SKU é livre em
 * runtime desde o PR G1 (super-admin cria módulo sem deploy), e o enum tem dois
 * valores. Uma terceira versão desta regra, `Capability[sku as keyof typeof
 * Capability]`, devolvia `undefined` para SKU criado em runtime; o Admin SDK
 * rejeita `undefined` dentro de array e a exceção caía no catch genérico DEPOIS
 * de a Stripe já ter criado o item — cobrança sem contrapartida local (#395).
 *
 * Origem: #699 — 2026-09-22 16:26 +0200 (movido de `apps/functions`).
 *
 * Usado por: `computeCapabilities` e `packages/types/src/tenantProvisioning.ts`.
 */
export function capabilityForSku(sku: string): string {
  return sku === Sku.BASE ? 'BASE_ACTIVE' : sku;
}

/**
 * Deriva `capabilities[]` dos itens que ainda valem.
 *
 * Vive aqui, e não mais em `apps/functions`, porque a #679 lhe deu um segundo
 * chamador que não é Function: o builder puro de provisionamento, que precisa
 * fazer o documento **nascer** com capabilities em vez de esperar o primeiro
 * webhook. O docstring histórico do caminho antigo continua em
 * `apps/functions/src/lib/billing/capabilities.ts`, que passou a re-exportar.
 *
 * Quem decide "ainda vale" é `isBillableItem`, e isso é uma correção silenciosa
 * da versão anterior: ela testava `removed_at !== null`, o que **pulava** item
 * cujo campo simplesmente não existisse — e item `GRANT` nasce por um caminho
 * novo, onde essa distinção deixa de ser teórica. `pending_removal_at` não
 * derruba: o cliente pagou até o fim do ciclo.
 *
 * Origem: #699 — 2026-09-22 16:26 +0200.
 *
 * Usado por: Functions — `webhooks/handleSubscriptionUpdated.ts`,
 * `webhooks/handleAddonProrationPaid.ts`, `webhooks/asaas/asaasHandlers.ts`,
 * `billing/pendingRemoval.ts`, `postSale/itemWrites.ts`,
 * `asaas/installmentRenewal.ts`, `asaas/saleItems.ts`,
 * `admin/grantModuleToTenants.ts`, `admin/backfillSubscriptionCapabilities.ts`,
 * `lib/billing/capabilities.ts` (re-export), `lib/billing/commercialModeTransition.ts`;
 * `packages/types` `schemas.ts`, `assistedSale.ts`;
 * `scripts/audit-duplicate-addon-items.ts`.
 *
 * Dados: grava `billing/subscription.capabilities[]` — strings livres
 * (`BASE_ACTIVE` + SKUs), lidas pelos clientes como cache de menu.
 *
 * Histórico:
 *  - 2026-09-25 08:48 +0200 — (#737) item aguardando proporção não libera.
 */
export function computeCapabilities(items: readonly SubscriptionItemCapabilityFields[]): string[] {
  const caps = new Set<string>();
  for (const item of items) {
    if (!isBillableItem(item)) continue;
    // Cobrado sim, liberado não: a proporção avulsa ainda não foi paga (#737).
    if (isAwaitingProration(item)) continue;
    caps.add(capabilityForSku(item.sku));
  }
  return Array.from(caps);
}

/**
 * Forma mínima de assinatura para o predicado de capability. `capabilities`
 * é `optional()` no schema, então o campo pode não existir — e um read cru do
 * Firestore devolve `undefined` tanto para "documento velho" quanto para
 * "não comprou".
 */
export type SubscriptionCapabilityFields = {
  capabilities?: readonly string[] | null | undefined;
};

/**
 * **Fonte única** do predicado "este tenant tem este módulo?" — espelhado em
 * Kotlin (`MoreMenuItem.availabilityFor`, `ui/navigation/MoreMenu.kt`, testado
 * em `MoreMenuCapabilityTest`) e Swift (`SessionGates.has`), no molde do gate
 * de pagamento (#679 §14.3-2). Esta docstring citava `Subscription.hasCapability`
 * no Kotlin — esse nome nunca existiu lá (corrigido em 2026-10-04, #798).
 *
 * Origem: #699 — 2026-09-22 16:26 +0200.
 *
 * Usado por: nenhum consumidor TS encontrado em 2026-10-04 — o web
 * (`apps/app/src/routes/navItems.ts`) faz `capabilities?.includes(...)` inline
 * (ver #796). Os espelhos nativos é que estão em uso.
 *
 * Responde `false` para assinatura sem o campo. Isso é fail-closed, e é seguro
 * **no cliente apenas porque ninguém chama isto com dado que pode não ter
 * chegado**: capability é cache, não consulta, e quem decide o que mostrar usa
 * o último valor conhecido, nunca uma leitura fresca que pode falhar. Um menu
 * que dependesse da rede viraria oferta quando o sinal caísse no meio da
 * cozinha e voltaria depois — pior que qualquer um dos dois estados.
 */
export function hasCapability(sub: SubscriptionCapabilityFields | null | undefined, capability: string): boolean {
  const caps = sub?.capabilities;
  if (caps === null || caps === undefined) return false;
  return caps.includes(capability);
}

/**
 * Forma mínima de assinatura para os predicados de gateway — subconjunto
 * estrutural de `Subscription` (schemas.ts). Qualquer objeto com esses
 * campos serve (inclui reads crus do Firestore).
 */
export type SubscriptionGatewayFields = {
  external_subscription_id?: string | null | undefined;
  external_provider?: string | null | undefined;
};

/**
 * **Fonte única** do predicado "a Stripe (ou outro PSP) está cobrando este
 * tenant?" — verdadeiro quando há `external_subscription_id` não-vazio.
 * Antes vivia inline em 4+ grafias divergentes (`!= null`, `!== ''`,
 * truthy). A versão canônica é a mais estrita: string vazia conta como SEM
 * gateway (só endurece).
 *
 * ATENÇÃO: presença do id NÃO prova que o provider é real — um id sintético
 * legado (`sim_*` do finado `simulatePayment`, #128) satisfaz isto. Para
 * "é um gateway RECONHECIDO?", use `isRecognizedGateway`.
 *
 * Type guard genérico: no ramo positivo (e no negativo de um `if
 * (!isGatewayManaged(sub)) throw`) o TS estreita `external_subscription_id`
 * para `string`, preservando o narrowing que o check inline
 * (`if (!sub.external_subscription_id)`) dava de graça nos call-sites que
 * usam o id logo em seguida (ex.: chamada à Stripe).
 *
 * Origem: #130 — 2026-07-12 12:15 +0200.
 *
 * Usado por: Functions `webhooks/handleSubscriptionDeleted.ts`,
 * `billing/onInvoiceWritten.ts`, `billing/dailyBillingTick.ts`,
 * `admin/normalizeUnpaidSubscriptions.ts`, `lib/billing/courtesy.ts`,
 * `lib/billing/unpaidAsaasSale.ts`; `hasGatewayEntitlement`/`needsPayment`
 * (este módulo); `scripts/monitor-firestore-integrity.ts`. Espelho Android em
 * `AppViewModel.kt`.
 *
 * Dicas: para o PAYWALL use `needsPayment`, não isto — o anual parcelado do
 * Asaas (#755) não tem `sub_…` e passaria aqui como "sem gateway".
 */
export function isGatewayManaged<T extends SubscriptionGatewayFields>(
  sub: T,
): sub is T & { external_subscription_id: string } {
  const id = sub.external_subscription_id;
  return id !== undefined && id !== null && id !== '';
}

/**
 * **Fonte única** do predicado "o `external_subscription_id` vem de um
 * gateway que reconhecemos como fonte real de cobrança?" — verdadeiro quando
 * `external_provider` é `stripe` ou `asaas` (#748) OU o id tem prefixo `sub_`
 * (formato Stripe e Asaas). Blinda consumidores que hoje tratam a mera presença do id como
 * prova de assinatura paga (#128): um artefato sintético (`sim_…` com
 * `external_provider: 'manual'`) NÃO passa. `isGatewayManaged ⊇
 * isRecognizedGateway`.
 *
 * Origem: #130 — 2026-07-12 12:15 +0200 (predicados de billing em
 * `@etiquetachef/types`).
 *
 * Usado por: Functions — `aggregations/revenuePredicates.ts` (MRR) e
 * `billing/dailyBillingTick.ts`; web `apps/app/src/auth/RequireTenant.tsx`
 * (telemetria `payment_gate_unrecognized_provider`). Espelhos à mão: Android
 * `ui/navigation/AppViewModel.kt` e iOS `Domain/.../SessionGates.swift`.
 *
 * Dicas: os espelhos Kotlin/Swift ainda só testam `stripe` + prefixo `sub_`.
 * Hoje equivalem para o Asaas (as assinaturas dele são `sub_…`, spike A0 #749),
 * e nos dois clientes o predicado só alimenta telemetria, não o paywall.
 *
 * Histórico:
 *  - 2026-07-12 12:15 +0200 — (#130) criado.
 *  - 2026-09-27 12:00 +0200 — (#750, A1 do #748) `asaas` reconhecido explicitamente.
 *  - 2026-10-12 11:56 +0200 — (#942) `appstore` reconhecido explicitamente.
 *  - 2026-10-14 13:31 +0200 — (#969) `googleplay` reconhecido explicitamente.
 */
export function isRecognizedGateway(sub: SubscriptionGatewayFields): boolean {
  const id = sub.external_subscription_id;
  if (id === undefined || id === null || id === '') return false;
  if (sub.external_provider === PaymentProvider.STRIPE) return true;
  // #748 — Asaas é gateway real de cobrança (assinatura `sub_…`, medida no
  // spike A0). Explícito, e não só pelo prefixo: se o Asaas mudar o formato,
  // a receita não pode sumir do MRR em silêncio.
  if (sub.external_provider === PaymentProvider.ASAAS) return true;
  // #942 — a App Store cobra de verdade (IAP do iOS); o id é `aps_<originalTransactionId>`,
  // sem prefixo `sub_`, então precisa do reconhecimento explícito.
  if (sub.external_provider === PaymentProvider.APPSTORE) return true;
  // #969 — o Google Play cobra de verdade (self-service no Android); o id é
  // `gps_<hash do purchaseToken>`, sem prefixo `sub_`.
  if (sub.external_provider === PaymentProvider.GOOGLEPLAY) return true;
  // Sem provider reconhecido (doc sem o campo, ou com outro valor): o prefixo
  // `sub_` é o último recurso. Um `sim_…` de `manual` cai aqui e é recusado.
  return id.startsWith('sub_');
}

/**
 * Forma mínima de fatura para {@link isGatewayOwnedInvoice} — subconjunto
 * estrutural de `Invoice` (schemas.ts); serve ao objeto parseado e ao read cru
 * do Firestore.
 */
export type InvoiceGatewayFields = {
  external_invoice_id?: string | null | undefined;
  external_provider?: string | null | undefined;
};

/**
 * Diz se a fatura PERTENCE a um gateway (espelho de uma cobrança do Stripe ou
 * do Asaas), e não é uma fatura só nossa.
 *
 * Origem: #795 (BIL-4) — 2026-10-03 14:39 +0200.
 *
 * Por quê: "Registrar pagamento manual" e "Cancelar fatura" do console mexiam
 * só no Firestore. Numa fatura do gateway isso é cobrança dupla: o Stripe
 * (Smart Retry) e o Asaas (cobrança no vencimento) seguem cobrando a fatura que
 * aqui já diz PAGA/CANCELADA, e o `invoice.paid` real é descartado como "já tem
 * pagamento" (`handleInvoicePaid`). Quem decide o destino dessa fatura é o
 * gateway; o espelho local só acompanha pelo webhook.
 *
 * Contrato (puro): `true` quando há `external_invoice_id` não-vazio, OU quando
 * `external_provider` é um gateway (qualquer valor não-vazio que não seja
 * `manual`). Fatura gerada localmente (sem os dois campos, ou `manual`) →
 * `false`. Fail-closed de propósito: na dúvida, conta como do gateway.
 *
 * Usado por: Functions — `admin/recordManualPayment.ts` e
 * `admin/cancelInvoice.ts` (recusam com `gateway_owned_invoice`); admin web —
 * `features/billing/RecordManualPaymentDialog.tsx` e `CancelInvoiceDialog.tsx`
 * (avisam antes do envio). O `dailyBillingTick` tem o inverso só pelo id
 * (`isLocalOrphanInvoice`, B-126).
 *
 * Dados — `tenants/{tenantId}/invoices/{invoiceId}` (`InvoiceSchema`), só os
 * campos lidos. Toda fatura espelhada nasce em `createMirroredInvoice`
 * (`billing/webhooks/gatewayDocs.ts`) com os dois preenchidos:
 *   { "external_provider": "stripe", "external_invoice_id": "in_000000000000" }
 *   { "external_provider": "asaas",  "external_invoice_id": "pay_000000000000" }
 *   { "external_provider": "asaas",  "external_invoice_id": "asaas_renewal_1790000000000" }
 *     // renovação recusada do anual parcelado (#755): resolve-se pelo link de
 *     // recuperação, não por baixa local — também conta como do gateway.
 *
 * Issues: #795 (BIL-4), #126 (B-126: fatura órfã local).
 */
export function isGatewayOwnedInvoice(invoice: InvoiceGatewayFields): boolean {
  const id = invoice.external_invoice_id;
  if (typeof id === 'string' && id !== '') return true;
  const provider = invoice.external_provider;
  return typeof provider === 'string' && provider !== '' && provider !== PaymentProvider.MANUAL;
}

/**
 * Forma mínima para o gate de pagamento: o id do gateway mais o tipo
 * comercial (#679 §4.5). Estrutural — serve ao objeto parseado e ao read cru.
 */
export type SubscriptionPaymentGateFields = SubscriptionGatewayFields & {
  commercial_mode?: CommercialMode | null | undefined;
  /** #755 — id do parcelamento anual vigente no Asaas (UUID). */
  installment_plan_id?: string | null | undefined;
};

/**
 * O tenant tem uma cobrança VIGENTE no gateway: uma assinatura
 * (`external_subscription_id`) ou um anual parcelado (`installment_plan_id`).
 *
 * Origem: #755 — 2026-09-28 09:52 +0200 (A6 do #748).
 *
 * Por quê: o anual parcelado do Asaas é uma cobrança parcelada paga de uma
 * vez no cartão, sem `sub_…` (spike A0 item 6, e a A6 em `docs/billing/asaas.md`).
 * Se o gate continuasse só no `external_subscription_id`, o cliente que pagou
 * o ano inteiro ficaria no paywall. É um predicado À PARTE de propósito:
 * `isGatewayManaged` responde "há assinatura no gateway para operar?" e é
 * usado por pós-venda, tick e cancelamento, que precisam do `sub_…`; trocar a
 * semântica dele mudaria todos esses caminhos em silêncio.
 *
 * Contrato: puro. Id vazio conta como ausente nos dois campos (a mesma régua
 * de `isGatewayManaged`).
 *
 * Usado por: `needsPayment` (este módulo, e com ele o paywall web,
 * `getMyPendingCheckoutLink`, `unpaidAssistedSales`, `courtesyMigration`);
 * `apps/functions/src/lib/billing/unpaidAsaasSale.ts`. Espelhos à mão:
 * Android `needsPaymentFor` (`AppViewModel.kt`) e iOS `needsPaymentFor`
 * (`SessionGates.swift`).
 *
 * Issues: #755 (A6), #748 (épico).
 */
export function hasGatewayEntitlement(sub: SubscriptionPaymentGateFields): boolean {
  if (isGatewayManaged(sub)) return true;
  const plan = sub.installment_plan_id;
  return plan !== undefined && plan !== null && plan !== '';
}

/**
 * Tipo comercial efetivo: o gravado, ou `DIRECT` quando ausente. Só em
 * memória — o Firestore não casa igualdade com campo ausente (C5), então
 * nenhuma query pode depender deste default antes do backfill da F7.
 *
 * Origem: #700 (#679 §4.5) — 2026-09-22 22:23 +0200.
 *
 * Usado por: `needsPayment` (este módulo), `schemas.ts`, `enums.ts`; web
 * `apps/app/src/auth/RequireTenant.tsx`. Para RELATAR use
 * `effectiveCommercialMode`, que ainda lê o cupom legado.
 */
export function commercialModeOf(sub: {
  commercial_mode?: CommercialMode | null | undefined;
}): CommercialMode {
  return sub.commercial_mode ?? CommercialMode.DIRECT;
}

/**
 * **Fonte única** do gate de pagamento (#679 §4.5), espelhada em Kotlin
 * (`needsPaymentFor`, `AppViewModel.kt`) e Swift (`needsPaymentFor`,
 * `SessionGates.swift`), com a mesma tabela de casos nas três.
 *
 * Só a venda DIRETA espera pagamento: cortesia, parceiro e demo não têm
 * assinatura no nosso gateway, e é o tipo comercial — não a presença de um
 * `sub_…` com cupom de 100 % — que os libera. Até aqui a cortesia passava por
 * ter uma assinatura Stripe real com cupom; a migração da F7 cancela essa
 * assinatura, e sem este predicado o tenant cairia no paywall.
 *
 * "Sem pagamento" é `!isGatewayManaged` — id ausente, `null` **ou vazio**.
 * É a régua que Android e iOS já aplicavam (os dois normalizam `''` para nulo
 * ao ler o documento); o web era o único que liberava um id vazio. Nenhum
 * documento de produção tem `''` (lido em 2026-09-22), então unificar não muda
 * o gate de ninguém — e impede que as plataformas discordem no dia em que um
 * aparecer.
 *
 * Origem: #700 (#679 §4.5) — 2026-09-22 22:23 +0200.
 *
 * Arquitetura: é o **único** bloqueio de um tenant DIRECT cancelado. O
 * cancelamento (`cancelSubscription` + `handleSubscriptionDeleted`) apaga
 * `external_subscription_id` e isso fecha este gate — mas não muda
 * `tenants/{tid}.status` (segue `ACTIVE`, que é o eixo de suspensão, não de
 * pagamento) nem `capabilities` (seguem com `BASE_ACTIVE` e os módulos). Por
 * isso o servidor usa este mesmo predicado: `moduleActive`
 * (`apps/functions/src/lib/modules/requireModule.ts`) e o gêmeo `paywallOpen`
 * das rules de módulo (Firestore e Storage), desde a #1039.
 *
 * Usado por: web `apps/app` `auth/RequireTenant.tsx` (gate de rota que manda
 * para `/payment-pending`) e `routes/PaymentPendingPage.tsx` (paywall);
 * `apps/admin` `BillingCard.tsx`; Functions `billing/getMyPendingCheckoutLink.ts`,
 * `admin/courtesyMigration.ts`, `lib/billing/unpaidAssistedSales.ts` e
 * `lib/modules/requireModule.ts` (`paywallOpen`, gate de módulo no servidor,
 * #1039); `scripts/diag-tenant-billing.ts`; `scripts/e2e-sale/partner.ts` (o
 * `scripts/reset-test-tenant-payment.ts`, que também usava, saiu em 2026-10-13,
 * #935). Espelhos: Android `needsPaymentFor`
 * (`AppViewModel.kt`, `NeedsPaymentForTest`), iOS `needsPaymentFor`
 * (`SessionGates.swift`, `SessionGatesTests`).
 *
 * Falhas conhecidas:
 *  - 2026-10-27 (#1039): conferido num cancelamento real de produção — o
 *    gate fechava certo, mas só no cliente; membro de tenant cancelado ainda
 *    chamava callable de módulo e gravava pelas rules via SDK. Corrigido com
 *    os espelhos no servidor citados em Arquitetura.
 *
 * Dicas: ao mudar a tabela de casos, mude os três espelhos e os três testes no
 * mesmo commit — não há gate automático que compare as plataformas. Vale
 * também para os espelhos do servidor: `paywallOpen` em `requireModule.ts`,
 * `firestore.rules` e `storage.rules`. Para
 * conferir "o acesso deste tenant está bloqueado?" sem abrir o app,
 * `npx tsx scripts/diag-tenant-billing.ts <tenantId>` imprime o veredito
 * deste predicado (só leitura, produção).
 *
 * Histórico:
 *  - 2026-09-28 09:52 +0200 — (#755, A6) o anual parcelado do Asaas
 *    (`installment_plan_id`) também libera, via `hasGatewayEntitlement`.
 *  - 2026-10-27 11:04 +0100 — (#1039) documentado que é o único bloqueio
 *    pós-cancelamento.
 *  - 2026-10-27 11:17 +0100 — (#1039) espelhado no servidor (módulos).
 */
export function needsPayment(sub: SubscriptionPaymentGateFields): boolean {
  if (commercialModeOf(sub) !== CommercialMode.DIRECT) return false;
  // #755 (A6) — o anual parcelado não tem `sub_…`; o parcelamento vigente
  // libera o app como uma assinatura (`hasGatewayEntitlement`).
  return !hasGatewayEntitlement(sub);
}

/** Sinais de pagamento da assinatura lidos por `isUnpaidAssistedSale`. Estrutural. */
export type AssistedSalePaymentFields = {
  status?: string | null | undefined;
  external_subscription_id?: string | null | undefined;
  /** #755 — anual parcelado do Asaas: pago, mas sem `sub_…`. */
  installment_plan_id?: string | null | undefined;
  last_checkout?: unknown;
  first_paid_at?: number | null | undefined;
};

/**
 * #679 — o tenant é uma venda assistida que AINDA NÃO FOI PAGA? Enquanto for,
 * o único link válido é o da venda (composição negociada, gerado no CRM): o
 * link de catálogo cobraria o BASE de lista e ficaria pagável ao lado do da
 * venda. Um só predicado para o servidor (`createCheckoutSessionForTenant`,
 * recusa `assisted_sale_use_link`) e para o botão do admin — senão o botão
 * habilita o que o servidor recusa, ou o contrário.
 *
 * Depois do primeiro pagamento a venda acabou e o tenant é um cliente como
 * outro qualquer: se cancelar, volta pelo link de catálogo — o da venda exige
 * lead `OPEN`, e o lead já é `WON`. Qualquer um destes diz "já pagou":
 * - `status === CANCELLED`: só o fim de uma assinatura grava;
 * - `last_checkout` / `first_paid_at`: só o `checkout.session.completed` grava;
 * - `external_subscription_id`: assinatura no gateway. Ele é APAGADO no
 *   cancelamento e na ida para parceiro/cortesia, por isso não basta sozinho;
 * - `installment_plan_id`: anual parcelado do Asaas (#755, A6), que não tem
 *   `sub_…`.
 *
 * O `external_customer_id` NÃO serve: o link da venda o grava antes de o
 * cliente pagar. Doc de assinatura ausente conta como não paga.
 *
 * Origem: #679 — 2026-09-26 19:44 +0200 (auditoria da venda assistida em
 * produção).
 *
 * Usado por: Functions `billing/checkoutSessionCore.ts` (recusa
 * `assisted_sale_use_link`), `lib/billing/unpaidAssistedSales.ts`; admin
 * `features/billing/BillingActions.tsx`; CRM `customers/tabs/SubscriptionTab.tsx`.
 *
 * Dados: `tenants/{tenantId}.sales_origin` (presente só em tenant provisionado
 * pelo CRM) + `billing/subscription` (`status`, `external_subscription_id`,
 * `installment_plan_id`, `last_checkout`, `first_paid_at`).
 *
 * Histórico:
 *  - 2026-09-28 09:52 +0200 — (#755, A6) `installment_plan_id` conta como pago.
 */
export function isUnpaidAssistedSale(
  tenant: { sales_origin?: unknown },
  sub: AssistedSalePaymentFields | null | undefined,
): boolean {
  if (tenant.sales_origin == null) return false;
  if (!sub) return true;
  if (sub.status === SubscriptionStatus.CANCELLED) return false;
  if (sub.last_checkout != null || sub.first_paid_at != null) return false;
  if (typeof sub.external_subscription_id === 'string' && sub.external_subscription_id.length > 0) {
    return false;
  }
  // #755 (A6) — parcelamento anual vigente = já pagou (o `first_paid_at` o
  // acompanha, mas o id é o sinal que sobrevive a um doc migrado sem ele).
  if (typeof sub.installment_plan_id === 'string' && sub.installment_plan_id.length > 0) {
    return false;
  }
  return true;
}

/**
 * Forma mínima para o tipo comercial **efetivo**: o modo gravado mais o cupom
 * legado de cortesia. Estrutural — serve ao objeto parseado e ao read cru.
 */
export type SubscriptionCommercialFields = SubscriptionGatewayFields & {
  commercial_mode?: CommercialMode | null | undefined;
  discount?:
    | {
        is_lifetime_free?: boolean | null | undefined;
        lifetime_free_category?: LifetimeFreeCategory | null | undefined;
      }
    | null
    | undefined;
};

/**
 * Tipo comercial **para relatar e operar** (#679 F7, #738) — diferente de
 * `commercialModeOf`, que é o do gate.
 *
 * Até a etapa E1 da migração, a cortesia e os dois demos não têm
 * `commercial_mode` gravado: vivem de assinatura Stripe com cupom
 * `LIFETIME_FREE`. Ler só o campo os trataria como DIRECT — MRR inflado pelos
 * demos, lembrete de cobrança, webhook de renovação escrevendo no doc. Então,
 * sem o campo, o cupom decide: categoria `DEMO` → DEMO, qualquer outra →
 * COURTESY. Com o campo gravado, ele vence (depois da E3 o `discount` some).
 *
 * **Não usar no gate de pagamento.** Lá a cortesia legada passa pela presença
 * do `sub_…` (é o sinal que o app antigo lê), e `needsPayment` fica no modo
 * gravado — trocar aqui mudaria o gate de quem já está liberado por outro
 * caminho, sem ganho.
 *
 * Origem: #738 (#679 F7) — 2026-09-25 10:41 +0200.
 *
 * Usado por: Functions `webhooks/handleCheckoutCompleted.ts`,
 * `webhooks/handlerContext.ts`, `billing/checkoutSessionCore.ts`,
 * `aggregations/revenuePredicates.ts`, `admin/courtesyMigration.ts`,
 * `admin/changeTenantCommercialMode.ts`, `lib/billing/courtesy.ts`,
 * `lib/billing/commercialModeTransition.ts`, `lib/billing/directOperable.ts`;
 * CRM Functions `crmDemoTenants.ts`; `packages/types` `tenantDisplayStatus.ts`;
 * admin `BillingActions.tsx`, `ChangeCommercialModeDialog.tsx`,
 * `lib/firestoreTenantDetail.ts`; CRM `SubscriptionTab.tsx`.
 *
 * Dados: `billing/subscription.commercial_mode` (`CommercialMode`, pode faltar
 * em doc anterior à E1) e `discount.lifetime_free_category`
 * (`LifetimeFreeCategory`).
 */
export function effectiveCommercialMode(sub: SubscriptionCommercialFields): CommercialMode {
  if (sub.commercial_mode != null) return sub.commercial_mode;
  if (sub.discount?.is_lifetime_free === true) {
    return sub.discount.lifetime_free_category === LifetimeFreeCategory.DEMO
      ? CommercialMode.DEMO
      : CommercialMode.COURTESY;
  }
  return CommercialMode.DIRECT;
}

/** Forma mínima para `isDirectPaying`. */
export type DirectPayingFields = SubscriptionCommercialFields &
  LivemodeFields & {
    status?: string | null | undefined;
    /** #942 — trial grátis da App Store em curso: ainda não é receita. */
    trial_ends_at?: number | null | undefined;
  };

/**
 * **Fonte única** de "este tenant é receita direta?" para todo RELATÓRIO
 * (#679 §4.5, D15): venda DIRETA, assinatura ACTIVE, num gateway reconhecido
 * e fora do test mode. MRR, ARPT, `tenants.active`, new MRR, recebíveis e os
 * drilldowns de cada um usam este predicado — e só ele, para o número e a
 * lista que o explica não discordarem (lição da #463).
 *
 * Cortesia, demo e parceiro saem daqui por definição: não são receita que o
 * nosso gateway cobra. O parceiro ganha um card próprio de repasse (F8).
 *
 * Origem: #738 (#679 D15) — 2026-09-25 10:41 +0200.
 *
 * Usado por: Functions `aggregations/revenuePredicates.ts`,
 * `aggregations/aggregateDailyMetrics.ts`,
 * `billing/computeSubscriptionReceivables.ts`; admin
 * `dashboard-finance/subscriptionRevenue.ts`, `TopRevenueAccountsTable.tsx`;
 * `packages/types` `aggregations.ts`, `schemas.ts`.
 *
 * Dicas: é predicado de RELATÓRIO. Para operar (cobrar, reconciliar) não use —
 * assinatura de sandbox precisa ser operada (ver `isTestModeRecord`).
 */
export function isDirectPaying(sub: DirectPayingFields): boolean {
  return (
    effectiveCommercialMode(sub) === CommercialMode.DIRECT &&
    sub.status === SubscriptionStatus.ACTIVE &&
    isRecognizedGateway(sub) &&
    !isTestModeRecord(sub) &&
    // #942 — durante o trial da App Store nada foi cobrado; a receita só
    // começa quando o `DID_RENEW` limpa o campo.
    (sub.trial_ends_at === undefined || sub.trial_ends_at === null)
  );
}

/**
 * Forma mínima de um registro financeiro (Invoice/Payment) para o predicado
 * de modo — subconjunto estrutural. Serve tanto para o objeto parseado
 * quanto para um read cru do Firestore.
 */
export type LivemodeFields = { livemode?: boolean | undefined };

/**
 * **Fonte única** do predicado "este registro é do Stripe TEST MODE?" —
 * verdadeiro SÓ quando `livemode === false`.
 *
 * Campo AUSENTE ⇒ live. Registros anteriores ao carimbo do webhook (e os
 * pagamentos manuais lançados pelo admin) não têm `livemode`, e são receita
 * real: classificá-los como teste os apagaria dos relatórios.
 *
 * NÃO trocar por query `where('livemode', '==', true)` nem
 * `where('livemode', '!=', false)`: no Firestore **ambas** descartam docs em
 * que o campo não existe, ou seja, sumiriam com o legado legítimo. O filtro
 * tem de ser em memória, depois do parse. Mesma regra já aplicada em
 * `purgeTestModePayments` (`apps/functions/src/admin/reconcileSubscriptionFromGateway.ts`).
 *
 * **Onde aplicar — e onde NÃO** (#463, varredura de todos os leitores de valor
 * de assinatura). A linha divisória é RELATAR contra OPERAR:
 *
 * - **Relatar exclui teste.** Todo número que alguém lê como dinheiro do
 *   negócio: MRR, ARPT, `sales`, new MRR, recebíveis — e, junto com cada um, o
 *   DRILLDOWN que o explica. Filtrar o total e esquecer a lista é pior que não
 *   filtrar nenhum dos dois: o número e a explicação passam a discordar, e
 *   quem clica para entender encontra contradição. Aconteceu duas vezes aqui.
 * - **Operar NÃO filtra.** `dailyBillingTick`, `onPaymentWritten`, `mbgRefund`
 *   e afins tratam assinatura de sandbox como qualquer outra — ela precisa ser
 *   cobrada, reconciliada e estornada de verdade, senão o ambiente de teste
 *   deixa de testar. Filtrar por simetria ali quebraria o E2E.
 *
 * Origem: #258 — 2026-08-11 15:52 +0200 (webhook test-mode escrevendo em prod).
 *
 * Usado por: Functions `aggregations/revenuePredicates.ts`,
 * `aggregations/aggregateDailyMetrics.ts`,
 * `billing/computeSubscriptionReceivables.ts`, `billing/postSale/gateway.ts`,
 * `billing/providers/index.ts`, `webhooks/asaas/asaasHandlers.ts`,
 * `lib/billing/testModeTenants.ts`, `lib/billing/liveGatewayBilling.ts`,
 * `lib/gatewaySecrets.ts`; admin `dashboard-finance/subscriptionRevenue.ts`,
 * `lib/firestoreTenantDetail.ts`; `isDirectPaying` (este módulo).
 *
 * Dados: `livemode` em `billing/subscription`, `invoices/*` e `payments/*` —
 * carimbado pelo webhook a partir do `event.livemode` (Stripe) ou do ambiente
 * sandbox (Asaas, #748); ausente em registro manual e em legado.
 *
 * Falhas conhecidas:
 *  - 2026-09-12 (#463): `sales` e new MRR contavam TEST MODE; o drilldown foi
 *    filtrado depois do total — ver a régua RELATAR/OPERAR acima.
 */
export function isTestModeRecord(record: LivemodeFields): boolean {
  return record.livemode === false;
}

/**
 * Status de assinatura do gateway que contam como **viva** — ou seja, que
 * ainda geram cobrança ou mantêm acesso. Espelha o vocabulário do Stripe
 * (`Stripe.Subscription.Status`). Quando esta lista nasceu o Stripe era o único
 * gateway; o Asaas entrou em produção (BR) na #748, mas a varredura por
 * customer (`listLiveSubscriptions`) continua implementada só no provider
 * Stripe — a lista segue sendo vocabulário Stripe por isso.
 *
 * Origem: #337 — 2026-09-04 12:38 +0200.
 *
 * Usado por: Functions `billing/providers/stripe.ts` e `providers/types.ts`
 * (contrato de `listLiveSubscriptions`); `scripts/audit-orphan-stripe-subscriptions.ts`.
 *
 * Ficam de fora `canceled`, `incomplete` e `incomplete_expired`: só um
 * cancelamento real libera um novo checkout, e uma sub `incomplete` é um lead
 * que nunca pagou.
 *
 * Mora aqui, e não no provider, porque três lugares precisam da MESMA lista e
 * um deles não pode importar o SDK do Stripe: `providers/stripe.ts` (as duas
 * varreduras por customer), e `scripts/audit-orphan-stripe-subscriptions.ts`
 * (#337), que roda fora do workspace das Functions e fala com a REST API por
 * `fetch`. Listas divergentes aqui significariam uma assinatura órfã sendo
 * cobrada e não aparecendo na varredura.
 */
export const LIVE_GATEWAY_SUBSCRIPTION_STATUSES = [
  'active',
  'trialing',
  'past_due',
  'unpaid',
  'paused',
] as const;

export type LiveGatewaySubscriptionStatus =
  (typeof LIVE_GATEWAY_SUBSCRIPTION_STATUSES)[number];

/**
 * Fallback hardcoded para `pricingForCountry` quando o doc do Firestore
 * não existe (boot inicial / dev sem seed). **Não é fonte da verdade em
 * prod** — o catálogo canônico `BASE_{currency}` (espelho da Stripe) vence,
 * e `resolvePlanPriceWithExternalIds` loga `warn` ao cair aqui (#125). Os
 * valores devem espelhar os Prices ativos na Stripe; quando divergirem,
 * atualizar aqui E no seed. Os ciclos anuais NÃO seguem uma regra fixa
 * (`monthly × N`): cada moeda tem seu desconto próprio definido na Stripe.
 *
 * Origem: sem issue — commit 20d1bdbea, 2026-05-04 23:12 +0200.
 *
 * Usado por: Functions `lib/billing/planPricing.ts` (`fallbackPricing`, só
 * quando `/config/planPrices/items/*` não tem o doc). `pricingForCountry` e
 * `pricingOptionsForCountry` abaixo são wrappers sem consumidor.
 *
 * Dados: `amount` em UNIDADES decimais (ver `PlanPricing`). Só mensal e anual —
 * trimestral/semestral não têm preço de lista (`isCatalogBillingCycle`).
 *
 * Histórico:
 *  - 2026-07-10 18:52 +0200 — (#125) BR alinhado ao catálogo canônico.
 *  - 2026-07-18 20:31 +0200 — reajuste BRL 348/2498.
 */
export const STANDARD_PLAN_PRICING_FALLBACK: Record<Country, { monthly: PlanPricing; yearly: PlanPricing }> = {
  BR: {
    // #125: alinhado aos Prices ATIVOS na Stripe LIVE — R$ 348,00/mês
    // (`price_1TucuY…`, 34800) e R$ 2.498,00/ano (`price_1Tucus…`, 249800),
    // vigentes desde 2026-07-18 (ledger: issue #54).
    monthly: { currency: Currency.BRL, amount: 348.0, billing_cycle: BillingCycle.MONTHLY },
    yearly: { currency: Currency.BRL, amount: 2498.0, billing_cycle: BillingCycle.YEARLY },
  },
  MX: {
    monthly: { currency: Currency.USD, amount: 49.0, billing_cycle: BillingCycle.MONTHLY },
    yearly: { currency: Currency.USD, amount: 599.0, billing_cycle: BillingCycle.YEARLY },
  },
  US: {
    monthly: { currency: Currency.USD, amount: 49.0, billing_cycle: BillingCycle.MONTHLY },
    yearly: { currency: Currency.USD, amount: 599.0, billing_cycle: BillingCycle.YEARLY },
  },
  CA: {
    monthly: { currency: Currency.USD, amount: 49.0, billing_cycle: BillingCycle.MONTHLY },
    yearly: { currency: Currency.USD, amount: 599.0, billing_cycle: BillingCycle.YEARLY },
  },
  IT: {
    monthly: { currency: Currency.EUR, amount: 39.0, billing_cycle: BillingCycle.MONTHLY },
    yearly: { currency: Currency.EUR, amount: 499.0, billing_cycle: BillingCycle.YEARLY },
  },
};

/**
 * Resolve o preço do plano padrão para um país + ciclo a partir do
 * fallback hardcoded. Para usar o valor canônico de Firestore, ler
 * `/config/planPrices/items/{sku}_{currency}` direto e cair neste fallback
 * apenas se o doc não existir (é o que `planPricing.ts` faz — sem passar por
 * aqui).
 *
 * Origem: sem issue — commit 2ae71001f, 2026-04-30 17:38 +0200 (plano único).
 *
 * Usado por: nenhum consumidor encontrado em 2026-10-04 (candidato a remoção —
 * ver #796).
 */
export function pricingForCountry(country: Country, cycle: BillingCycle = BillingCycle.MONTHLY): PlanPricing {
  const opts = STANDARD_PLAN_PRICING_FALLBACK[country];
  return cycle === BillingCycle.YEARLY ? opts.yearly : opts.monthly;
}

/**
 * Conveniência para a tela de seleção de plano: retorna mensal + anual.
 *
 * **Apenas fallback** — a fonte da verdade do preço é o PSP (Stripe), espelhado
 * em `/config/planPrices/items/{sku}_{currency}`. Os apps leem do Firestore;
 * este fallback hardcoded só cobre boot/dev sem seed no backend
 * (`resolvePlanPriceWithExternalIds`). Descontos não existem mais no plano —
 * só por cupom (owner 2026-06-08), por isso `yearlySavings` foi removido.
 *
 * Origem: sem issue — commit 20d1bdbea, 2026-05-04 23:12 +0200.
 *
 * Usado por: nenhum consumidor encontrado em 2026-10-04 (candidato a remoção —
 * ver #796).
 */
export function pricingOptionsForCountry(country: Country): { monthly: PlanPricing; yearly: PlanPricing } {
  return STANDARD_PLAN_PRICING_FALLBACK[country];
}

/**
 * Câmbio fixo por moeda para conversão MRR → USD-equivalente nos
 * agregados do BI. Hardcoded e revisado trimestralmente — não puxa de
 * API de FX em runtime (estabilidade e custo zero por execução do
 * agregador). Última revisão: 2026-04-30.
 *
 * Origem: sem issue — commit 27d76e711, 2026-05-04 15:15 +0200
 * (`DailyAggregateSchema` v1).
 *
 * Usado por: Functions `aggregations/aggregateDailyMetrics.ts`; admin
 * `dashboard-finance/RevenueByCurrencyTable.tsx`; `priceCentsToUsdCents`.
 *
 * Dicas: mudar uma taxa muda o USD-equivalente dos agregados futuros, não dos
 * já gravados em `daily_aggregates` — comparar séries exige backfill
 * (`apps/functions/scripts/backfill-aggregations.ts`).
 */
export const FX_TO_USD: Record<Currency, number> = {
  USD: 1.0,
  BRL: 0.2,
  EUR: 1.08,
} as const;

/**
 * Converte um valor decimal (unidade inteira da moeda) para USD-equivalente
 * decimal usando `FX_TO_USD`. Banker's rounding não compensa o custo
 * de complexidade — diferença sub-cent é ruído no agregado global.
 *
 * Origem: sem issue — commit 20d1bdbea, 2026-05-04 23:12 +0200.
 *
 * Usado por: nenhum consumidor encontrado em 2026-10-04 (candidato a remoção —
 * ver #796). Os agregadores usam `priceCentsToUsdCents`.
 */
export function amountToUsd(amount: number, currency: Currency): number {
  return Math.round(amount * FX_TO_USD[currency] * 100) / 100;
}

/**
 * Converte centavos da moeda local para centavos USD usando `FX_TO_USD`.
 * Chamava-se "legado" nesta docstring, mas é a conversão que os agregadores
 * de BI usam de fato (centavos inteiros evitam erro de ponto flutuante em
 * somatório); a "nova", `amountToUsd`, ficou sem consumidor.
 *
 * Origem: sem issue — commit 27d76e711, 2026-05-04 15:15 +0200
 * (`DailyAggregateSchema` v1).
 *
 * Usado por: Functions `aggregations/aggregateDailyMetrics.ts`,
 * `aggregations/listDrilldown.ts`, `apps/functions/scripts/backfill-aggregations.ts`;
 * admin `dashboard-finance/subscriptionRevenue.ts`, `RevenueByCurrencyTable.tsx`.
 */
export function priceCentsToUsdCents(priceCents: number, currency: Currency): number {
  return Math.round(priceCents * FX_TO_USD[currency]);
}

/**
 * Formatação canônica de moeda para UI. Wrapper sobre `Intl.NumberFormat`
 * com fallback seguro caso o locale não esteja disponível no runtime.
 *
 * **Display rule (cliente final):** valores com centavos zerados
 * (`.00`) são formatados sem os centavos — `R$ 997` em vez de
 * `R$ 997,00`, `$ 197` em vez de `$ 197.00`. Pricing anual termina
 * em `,00` por design (psicologia de preço); mensal mantém centavos
 * (`R$ 19,97`) porque os centavos são significativos.
 *
 * Origem: sem issue — commit 20d1bdbea, 2026-05-04 23:12 +0200.
 *
 * Contrato: `amount` em UNIDADES decimais — quem tem cents converte antes com
 * `priceCentsToUnits`. `locale` BCP-47; locale desconhecido cai no fallback
 * `"<CUR> 1234.56"` em vez de lançar.
 *
 * Usado por: `ui-shared` (`MoneyDisplay`, `PlanCard`); admin (`MoneyDisplay`,
 * `RequestRefundDialog`); `apps/app` `SubscriptionItemsCard.tsx`; CRM
 * (`lib/format.ts` e páginas de performance/home/assinatura); Functions
 * `email/templates/_layout/formatHelpers.ts`; `packages/types` `crm.ts`,
 * `schemas.ts`.
 */
export function formatCurrency(amount: number, currency: Currency, locale: string): string {
  const hasCents = Math.round(amount * 100) % 100 !== 0;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);
  } catch {
    return hasCents ? `${currency} ${amount.toFixed(2)}` : `${currency} ${amount.toFixed(0)}`;
  }
}

/**
 * Inverso de `formatCurrency` para inputs de UI (super-admin editando
 * preço do plano). Aceita `R$ 100,00`, `100,00`, `100.00`, `100`.
 * Lança se o input não puder ser parseado.
 *
 * Origem: sem issue — commit 20d1bdbea, 2026-05-04 23:12 +0200.
 *
 * Usado por: nenhum consumidor encontrado em 2026-10-04 (candidato a remoção —
 * ver #796) — desde que o preço passou a ser espelho do PSP (2026-06-08), o
 * super-admin não edita valor à mão.
 */
export function parseCurrencyAmount(input: string, currency: Currency): number {
  const cleaned = input
    .replace(/\s+/g, '')
    .replace(currency, '')
    .replace(/[€$R]/g, '');
  // Heurística: se houver "," e "." → "," é decimal pt-BR e "." é milhar.
  // Se houver só "," → decimal pt-BR. Caso contrário "." é decimal en-US.
  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  let normalized = cleaned;
  if (hasComma && hasDot) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (hasComma) {
    normalized = cleaned.replace(',', '.');
  }
  const value = Number(normalized);
  if (!Number.isFinite(value)) {
    throw new Error(`Invalid currency input: "${input}"`);
  }
  return value;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A assinatura nasceu de uma venda assistida (#679 F1): itens com preço
 * NEGOCIADO (`price_data`), cobrados numa composição que só o consultor muda.
 *
 * Os caminhos de catálogo do self-service (troca de ciclo, add-on pelo app,
 * reajuste em lote) montam a assinatura a partir do Price de LISTA — aplicados
 * a uma venda negociada, trocariam o preço combinado pelo de lista e, na troca
 * de ciclo, derrubariam os módulos (o agendamento na Stripe é montado só com o
 * primeiro item). Por isso todos recusam quando isto é verdade; a mudança é do
 * consultor (F5).
 *
 * Origem: #700 (#679 F1) — 2026-09-23 10:06 +0200.
 *
 * Usado por: nenhum consumidor encontrado em 2026-10-04 (candidato a remoção —
 * ver #796). Os caminhos que recusam comparam `pricing_source` com
 * `PricingSource.CRM_SALE` direto (ex.: `postSale/context.ts`).
 */
export function isNegotiatedSubscription(sub: { pricing_source?: string | null | undefined }): boolean {
  return sub.pricing_source === 'CRM_SALE';
}

/**
 * Os ciclos que têm preço de LISTA no catálogo
 * (`/config/planPrices/items/{sku}_{currency}`) — os
 * únicos que o self-service vende, troca e reajusta. Trimestral e semestral
 * (D13) só existem negociados na venda assistida; os caminhos de catálogo
 * (troca de ciclo pelo cliente, cupom, reajuste em lote) os recusam em vez de
 * os tratar como mês ou ano.
 *
 * Origem: #700 (D13) — 2026-09-22 23:47 +0200.
 *
 * Usado por: Functions `lib/billing/planPricing.ts` (`isCatalogBillingCycle`).
 */
export type CatalogBillingCycle = typeof BillingCycle.MONTHLY | typeof BillingCycle.YEARLY;

/**
 * Type guard de `CatalogBillingCycle`: só MONTHLY e YEARLY têm preço de lista
 * (ver o tipo acima); trimestral e semestral devolvem `false`.
 *
 * Origem: #700 (D13) — 2026-09-22 23:47 +0200.
 *
 * Usado por: Functions `lib/billing/planPricing.ts` — os caminhos de catálogo
 * (troca de ciclo, cupom, reajuste em lote) recusam o ciclo negociado.
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function isCatalogBillingCycle(cycle: BillingCycle): cycle is CatalogBillingCycle {
  return cycle === BillingCycle.MONTHLY || cycle === BillingCycle.YEARLY;
}

/**
 * Quantos meses cobre um ciclo de cobrança (#679 D13). **Fonte única** da
 * aritmética de ciclo: período, vencimento, equivalente mensal e MRR partem
 * daqui. O `switch` é exaustivo de propósito — um ciclo novo no enum reprova
 * o typecheck até alguém dizer quantos meses ele tem, em vez de cair calado
 * num `else` que o trataria como mês (foi o que os ternários
 * `YEARLY ? ano : mês` fariam com o trimestral: cobrar na data errada).
 *
 * Origem: #700 (D13) — 2026-09-22 23:47 +0200.
 *
 * Usado por: `addBillingCycle`/`monthlyAmountCents` (este módulo); Functions
 * `billing/receivablesAnalysis.ts`, `lib/billing/period.ts`; admin
 * `dashboard-finance/LifetimeFreeCard.tsx`; `packages/types` `enums.ts`,
 * `partnerRemittance.ts`. Espelho Android: `domain/billing/Subscription.kt`.
 */
export function monthsInCycle(cycle: BillingCycle): 1 | 3 | 6 | 12 {
  switch (cycle) {
    case BillingCycle.MONTHLY:
      return 1;
    case BillingCycle.QUARTERLY:
      return 3;
    case BillingCycle.SEMIANNUAL:
      return 6;
    case BillingCycle.YEARLY:
      return 12;
  }
}

/**
 * Valor do ciclo convertido para o equivalente mensal (#679 D15): o anual
 * dividido por 12, o trimestral por 3. É a régua do MRR — somar o valor do
 * ciclo inteiro contava o demo anual (€ 499) como se fosse mensalidade.
 * Sem desconto: quem precisa dele (recebíveis) aplica por cima.
 *
 * Origem: #738 (#679 D15) — 2026-09-25 10:41 +0200.
 *
 * Contrato: `amountCents` e retorno em CENTS; arredonda para o centavo inteiro.
 *
 * Usado por: Functions `aggregations/revenuePredicates.ts` (MRR); admin
 * `dashboard-finance/subscriptionRevenue.ts` (o drilldown do mesmo número).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function monthlyAmountCents(amountCents: number, cycle: BillingCycle): number {
  return Math.round(amountCents / monthsInCycle(cycle));
}

/**
 * Avança `fromMs` pelos meses do ciclo, sem transbordar o fim do mês: `31 jan`
 * + 1 mês = `28/29 fev`, nunca `3 mar` (M-20). O anual usa `setUTCFullYear`
 * direto (`29 fev` → `1 mar` em ano não bissexto, decisão antiga de produto).
 *
 * Fonte única da aritmética de período: o `advancePeriod` das Functions
 * (`lib/billing/period.ts`) delega para cá. Antes eram duas implementações, e
 * esta ingênua (`setUTCMonth(mês + n)`) transbordava o fim do mês que a outra
 * corrigia.
 *
 * Origem: #700 (M-20) — 2026-09-23 10:27 +0200.
 *
 * Contrato: epoch ms UTC in/out; a conta é em UTC, então "dia do mês" é o dia
 * UTC — uma cobrança às 23h de Brasília pode cair no dia seguinte.
 *
 * Usado por: `nextDueAt` (este módulo); Functions `lib/billing/period.ts`,
 * `webhooks/asaas/asaasHandlers.ts`, `asaas/installmentRenewal.ts`;
 * `packages/types` `postSale.ts`; CRM `subscription/SaleSubscriptionPage.tsx`.
 */
export function addBillingCycle(fromMs: number, cycle: BillingCycle): number {
  const date = new Date(fromMs);
  const next = new Date(date.getTime());
  if (cycle === BillingCycle.YEARLY) {
    next.setUTCFullYear(date.getUTCFullYear() + 1);
    return next.getTime();
  }
  // Dia 1 primeiro, para a troca de mês não transbordar.
  next.setUTCFullYear(date.getUTCFullYear(), date.getUTCMonth() + monthsInCycle(cycle), 1);
  // Último dia do mês alvo: dia 0 do mês seguinte, lido de `next` já
  // normalizado — somar o mês relativo ao ano de ORIGEM ao ano novo apontava
  // um ano à frente (o trimestral de 30/nov caía em 1º/mar).
  const lastDayOfTargetMonth = new Date(
    Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0),
  ).getUTCDate();
  next.setUTCDate(Math.min(date.getUTCDate(), lastDayOfTargetMonth));
  return next.getTime();
}

/**
 * O próximo `due_at` de uma assinatura: `current_period_end` + um ciclo.
 *
 * Origem: sem issue — commit 20d1bdbea, 2026-05-04 23:12 +0200.
 *
 * Usado por: Functions `lib/billing/period.ts`; CRM Functions
 * `lib/salesScope.ts`; CRM `sales/SalesPage.tsx`; admin `lib/firestoreTenants.ts`;
 * `packages/types` `crm.ts`.
 */
export function nextDueAt(opts: { current_period_end: number; billing_cycle: BillingCycle }): number {
  return addBillingCycle(opts.current_period_end, opts.billing_cycle);
}

/**
 * Quantos dias inteiros uma fatura está vencida em relação a `now`.
 * Retorna 0 se ainda não venceu. Usa truncamento (não round) para o
 * scheduler reportar "1 dia atrasado" só após 24h cheias.
 *
 * Origem: sem issue — commit 20d1bdbea, 2026-05-04 23:12 +0200.
 *
 * Usado por: Functions `billing/dailyBillingTick.ts`,
 * `email/templates/billing/invoiceOverdue.tsx`, `email/preview/main.ts`;
 * `packages/types` `email.ts`.
 */
export function daysOverdue(opts: { due_at: number; now?: number }): number {
  const now = opts.now ?? Date.now();
  if (opts.due_at >= now) return 0;
  return Math.floor((now - opts.due_at) / ONE_DAY_MS);
}

/**
 * Quantos dias faltam até o vencimento. Negativo se já venceu.
 *
 * Origem: sem issue — commit 20d1bdbea, 2026-05-04 23:12 +0200.
 *
 * Usado por: Functions `billing/dailyBillingTick.ts` (lembretes,
 * `due_soon_days_before`), `email/templates/billing/invoiceDueSoon.tsx`,
 * `email/preview/main.ts`; `packages/types` `email.ts`.
 */
export function daysUntilDue(opts: { due_at: number; now?: number }): number {
  const now = opts.now ?? Date.now();
  return Math.floor((opts.due_at - now) / ONE_DAY_MS);
}
